import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { RISK_BADGE, STATUS_BADGE, STAGE_BADGE, PRIORITY_BADGE, RISK_DOT } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon } from '@hugeicons/core-free-icons';

// Dot-style badge (Tailwind UI pattern: colored dot + text)
const DOT_COLORS = {
  // Risk levels
  Bajo: 'bg-emerald-500',
  Medio: 'bg-amber-500',
  'Medio-Alto': 'bg-orange-500',
  Alto: 'bg-rose-500',
  'Muy alto': 'bg-rose-600',
  Crítico: 'bg-red-700',
  // Stages
  Idea: 'bg-gray-400',
  Exploración: 'bg-violet-400',
  Prefactibilidad: 'bg-violet-500',
  Detección: 'bg-gray-400',
  Registro: 'bg-violet-500',
  Evaluación: 'bg-amber-500',
  Decisión: 'bg-blue-500',
  POC: 'bg-cyan-500',
  Piloto: 'bg-cyan-500',
  Implementación: 'bg-blue-500',
  Producción: 'bg-emerald-500',
  Escalamiento: 'bg-emerald-600',
  Cierre: 'bg-gray-400',
  Monitoreo: 'bg-blue-500',
  Reevaluación: 'bg-orange-500',
  Retirada: 'bg-gray-400',
  // Status
  Pendiente: 'bg-gray-400',
  'En curso': 'bg-amber-500',
  Bloqueada: 'bg-rose-500',
  Completada: 'bg-emerald-500',
  Completado: 'bg-gray-400',
  Vencida: 'bg-red-600',
  Implementado: 'bg-emerald-500',
  Aprobado: 'bg-emerald-500',
  Aprobada: 'bg-emerald-500',
  // Demand intake status
  Nueva: 'bg-blue-500',
  'En análisis': 'bg-amber-500',
  Convertida: 'bg-emerald-500',
  Descartada: 'bg-gray-400',
  Duplicada: 'bg-gray-400',
  // Urgency
  Baja: 'bg-gray-400',
  Normal: 'bg-blue-500',
  Alta: 'bg-orange-500',
  Urgente: 'bg-rose-500',
  // Capability status
  'En diseño': 'bg-gray-400',
  'En construcción': 'bg-amber-500',
  // Provider alerts
  Activa: 'bg-rose-500',
  Resuelta: 'bg-emerald-500',
  'Falso positivo': 'bg-gray-400',
  // Health
  Verde: 'bg-emerald-500',
  Amarillo: 'bg-amber-500',
  Naranja: 'bg-orange-500',
  Rojo: 'bg-rose-500',
  'Sin actualización': 'bg-gray-400',
  // User management
  Activo: 'bg-emerald-500',
  Inactivo: 'bg-gray-400',
  Microsoft: 'bg-blue-500',
  Local: 'bg-gray-400',
  // Priorización de portafolio (frente 3) — clases en minúscula del backend
  impulsar: 'bg-emerald-500',
  madurar: 'bg-amber-500',
  cosechar: 'bg-[#307FE2]',
  esperar: 'bg-slate-400',
  detener: 'bg-rose-500',
  // Beneficios esperados de la iniciativa
  Planeado: 'bg-gray-400',
  Logrado: 'bg-emerald-500',
  'En riesgo': 'bg-amber-500',
  // Hitos de seguimiento de la iniciativa
  'En desarrollo': 'bg-amber-500',
  'En paralelo': 'bg-[#307FE2]',
  'En producción': 'bg-emerald-500',
};

function DotBadge({ label, dotColor, className }) {
  const color = dotColor || 'bg-gray-400';
  return (
    <div className={cn('flex items-center gap-x-1.5', className)}>
      <div className={cn('flex-none rounded-full p-1', color + '/20')}>
        <div className={cn('size-1.5 rounded-full', color)} />
      </div>
      <span className="text-xs/5 text-muted-foreground font-medium">{label || '—'}</span>
    </div>
  );
}

export const RiskBadge = ({ level, className }) => (
  <DotBadge label={level} dotColor={DOT_COLORS[level]} className={className} />
);

export const StageBadge = ({ stage, className }) => (
  <DotBadge label={stage} dotColor={DOT_COLORS[stage]} className={className} />
);

export const StatusBadge = ({ status, className }) => (
  <DotBadge label={status} dotColor={DOT_COLORS[status]} className={className} />
);

export const PriorityBadge = ({ priority, className }) => (
  <DotBadge label={priority} dotColor={DOT_COLORS[priority] || 'bg-gray-400'} className={className} />
);

// Etiquetas legibles para las clases de priorización de portafolio (frente 3).
const PORTFOLIO_PRIORITY_LABEL = {
  impulsar: 'Impulsar',
  madurar: 'Madurar',
  cosechar: 'Cosechar',
  esperar: 'Esperar',
  detener: 'Detener',
};

// PortfolioPriorityBadge muestra la clase de priorización de una iniciativa con
// el mismo patrón de punto que el resto de badges. Si no hay clase, muestra un
// estado neutro "Sin priorizar".
export const PortfolioPriorityBadge = ({ value, className }) => {
  const label = value ? PORTFOLIO_PRIORITY_LABEL[value] : null;
  return (
    <DotBadge
      label={label || 'Sin priorizar'}
      dotColor={label ? DOT_COLORS[value] : 'bg-gray-300'}
      className={className}
    />
  );
};

export const UrgencyBadge = ({ urgency, className }) => (
  <DotBadge label={urgency} dotColor={DOT_COLORS[urgency] || 'bg-gray-400'} className={className} />
);

export const HealthBadge = ({ health, className }) => (
  <DotBadge label={health} dotColor={DOT_COLORS[health] || 'bg-gray-400'} className={className} />
);

// PageHeader del título de página. Las props `backTo`/`backLabel` son
// OPCIONALES y retrocompatibles: cuando se pasa `backTo` se muestra un enlace
// "volver" accesible (ancla real + icono aria-hidden) sobre el título; sin
// ellas el render es idéntico al original. Pensado para reutilizarse en más
// páginas que necesiten navegación de retorno.
export const PageHeader = ({ title, subtitle, children, testId, backTo, backLabel = 'Volver' }) => (
  <div className="flex flex-col mb-8" data-testid={testId}>
    {backTo && (
      <Link
        to={backTo}
        aria-label={backLabel}
        className="inline-flex items-center gap-1.5 self-start text-xs text-muted-foreground hover:text-foreground transition-colors mb-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
        <HugeiconsIcon icon={ArrowLeft01Icon} size={14} strokeWidth={1.5} aria-hidden="true" />
        {backLabel}
      </Link>
    )}
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap shrink-0">{children}</div>}
    </div>
  </div>
);

export const EmptyState = ({ icon, title, subtitle }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    {icon && (
      <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center mb-4">
        <HugeiconsIcon icon={icon} className="h-6 w-6 text-muted-foreground" />
      </div>
    )}
    <p className="font-medium text-foreground">{title}</p>
    {subtitle &&
      (typeof subtitle === 'string' ? (
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">{subtitle}</p>
      ) : (
        <div className="text-sm text-muted-foreground mt-1 max-w-md">{subtitle}</div>
      ))}
  </div>
);

// Explicación estructurada para estados vacíos: qué es la sección, por qué
// importa, qué ocurre si queda vacía y qué se logra al completarla. La IA es
// nueva para muchos usuarios; no dejamos nada a la imaginación.
export const EmptyExplainer = ({ what, whyItMatters, ifEmpty, whenDone }) => (
  <div className="mt-1 space-y-2.5 text-left">
    {what && <p className="text-sm text-muted-foreground leading-relaxed">{what}</p>}
    <ul className="space-y-2">
      {whyItMatters && (
        <li className="flex gap-2">
          <span className="mt-1.5 size-1.5 rounded-full bg-secondary shrink-0" />
          <span className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Por qué importa: </span>
            {whyItMatters}
          </span>
        </li>
      )}
      {ifEmpty && (
        <li className="flex gap-2">
          <span className="mt-1.5 size-1.5 rounded-full bg-risk-medium shrink-0" />
          <span className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Si queda vacío: </span>
            {ifEmpty}
          </span>
        </li>
      )}
      {whenDone && (
        <li className="flex gap-2">
          <span className="mt-1.5 size-1.5 rounded-full bg-success shrink-0" />
          <span className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Qué logramos: </span>
            {whenDone}
          </span>
        </li>
      )}
    </ul>
  </div>
);

export const StatCard = ({ icon, label, value, hint, tone = 'default', testId }) => (
  <div
    className="bg-card rounded-xl border border-border shadow-card p-5 flex flex-col gap-3 transition-shadow hover:shadow-float"
    data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      {icon && (
        <div
          className={cn(
            'h-8 w-8 rounded-lg flex items-center justify-center',
            tone === 'danger'
              ? 'bg-risk-critical/10 text-risk-critical'
              : tone === 'warning'
                ? 'bg-risk-medium/10 text-risk-medium'
                : tone === 'accent'
                  ? 'bg-accent/10 text-accent'
                  : 'bg-primary/8 text-primary',
          )}>
          <HugeiconsIcon icon={icon} className="h-4 w-4" />
        </div>
      )}
    </div>
    <div className="mt-auto">
      <span className="font-display text-3xl font-semibold tabular text-foreground">{value}</span>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  </div>
);
